/**
 * Caregivers list + invite. Used inside the combined Settings page.
 */

import { useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { useMembers, type MemberWithId } from '@/hooks/useMembers';
import { useMyRole } from '@/hooks/useMyRole';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useInvites, useInvalidateInvites, type PendingInvite } from '@/hooks/useInvites';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import { Spinner } from '@/components/ui/Spinner';
import { changeMemberRole, createInvite, removeMember, revokeInvite, resendInvite } from '@/lib/callables';

interface Props {
  aid: string;
}

export function CaregiversSection({ aid }: Props) {
  const { members, loading } = useMembers(aid);
  const { invites, loading: invitesLoading } = useInvites(aid);
  const invalidateInvites = useInvalidateInvites();
  const auth = useAuth();
  const myUid = auth.state === 'signedIn' ? auth.user.uid : null;
  const myRole = useMyRole(aid);
  const isAdmin = myRole === 'admin';
  const myProfile = useUserProfile(myUid);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <p className="text-sm text-ink-soft">
          Caregivers can edit events and devices. Admins can also invite or remove others.
        </p>
        {isAdmin && (
          <InviteDialog
            aid={aid}
            onInviteSent={() => invalidateInvites(aid)}
          />
        )}
      </div>
      {loading || invitesLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="grid gap-3">
          {members.map((m) => {
            const isMe = m.id === myUid;
            const myEmail =
              auth.state === 'signedIn' ? auth.user.email ?? null : null;
            const myDisplayName =
              myProfile.state === 'present'
                ? myProfile.profile.displayName ?? null
                : null;
            const name = isMe
              ? myDisplayName || m.displayName || null
              : m.displayName || null;
            const email = isMe ? myEmail || m.email || null : m.email || null;
            const title = name || email || (isMe ? 'You' : capitaliseRole(m.role));
            const subtitleParts: string[] = [];
            if (isMe) subtitleParts.push('You');
            subtitleParts.push(capitaliseRole(m.role));
            if (name && email) subtitleParts.push(email);
            else if (!name && !email) subtitleParts.push(`ID ${shortUid(m.id)}`);
            return (
              <Card key={m.id}>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="truncate">{title}</CardTitle>
                      <CardDescription className="truncate">{subtitleParts.join(' · ')}</CardDescription>
                    </div>
                    {isAdmin && !isMe && <MemberActions aid={aid} member={m} />}
                  </div>
                </CardHeader>
              </Card>
            );
          })}
          {invites.map((invite) => (
            <PendingInviteRow
              key={invite.inviteId}
              invite={invite}
              isAdmin={isAdmin}
              onRevoked={() => invalidateInvites(aid)}
              onResent={() => invalidateInvites(aid)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PendingInviteRow({
  invite,
  isAdmin,
  onRevoked,
  onResent,
}: {
  invite: PendingInvite;
  isAdmin: boolean;
  onRevoked: () => void;
  onResent: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resentOk, setResentOk] = useState(false);

  async function onRevoke() {
    setBusy(true);
    setError(null);
    try {
      await revokeInvite({ inviteId: invite.inviteId });
      onRevoked();
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setBusy(true);
    setError(null);
    setResentOk(false);
    try {
      await resendInvite({ inviteId: invite.inviteId });
      setResentOk(true);
      onResent();
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  const isExpired = invite.status === 'expired';

  return (
    <Card key={invite.inviteId} className="opacity-80">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="truncate">{invite.email}</CardTitle>
            <CardDescription className="truncate">
              {capitaliseRole(invite.role)} · {isExpired ? 'Invite expired' : 'Invite pending'}
            </CardDescription>
          </div>
          {isAdmin && (
            <div className="flex shrink-0 flex-col items-end gap-1">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => void onResend()}
                >
                  {busy ? 'Sending…' : resentOk ? 'Sent ✓' : 'Resend invite'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-700 hover:bg-red-50"
                  disabled={busy}
                  onClick={() => void onRevoke()}
                >
                  Revoke
                </Button>
              </div>
              {error && (
                <span className="text-xs text-red-700">{error}</span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
    </Card>
  );
}

/** Per-row actions visible to admins, hidden for the admin's own
 * row (admins use AccountSection's "Leave this account" button to
 * remove themselves — same server-side endpoint, but the entry point
 * is intentionally on the danger-zone section). */
function MemberActions({ aid, member }: { aid: string; member: MemberWithId }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  async function onChangeRole(next: 'admin' | 'caregiver') {
    if (next === member.role) return;
    setBusy(true);
    setError(null);
    try {
      await changeMemberRole({ accountId: aid, uid: member.id, role: next });
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    setBusy(true);
    setError(null);
    try {
      await removeMember({ accountId: aid, uid: member.id });
      setConfirmingRemove(false);
    } catch (e) {
      setError(humanError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex shrink-0 flex-col items-stretch gap-1 sm:items-end">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <select
          className="h-8 rounded-md border border-paper-border bg-white px-2 text-xs text-ink disabled:opacity-50"
          value={member.role}
          onChange={(e) => void onChangeRole(e.target.value as 'admin' | 'caregiver')}
          disabled={busy}
        >
          <option value="caregiver">Caregiver</option>
          <option value="admin">Admin</option>
        </select>
        {confirmingRemove ? (
          <>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirmingRemove(false)}>
              Cancel
            </Button>
            <Button size="sm" variant="danger" disabled={busy} onClick={() => void onRemove()}>
              {busy ? 'Removing…' : 'Remove'}
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="text-red-700 hover:bg-red-50"
            disabled={busy}
            onClick={() => setConfirmingRemove(true)}
          >
            Remove
          </Button>
        )}
      </div>
      {error && (
        <span className="max-w-[18rem] text-right text-xs text-red-700">{error}</span>
      )}
    </div>
  );
}

function humanError(e: unknown): string {
  if (e instanceof Error) {
    // Firebase callable errors surface their `.message` as
    // "FirebaseError: ..." or expose the underlying server message.
    // Trim the prefix for readability.
    return e.message.replace(/^FirebaseError:\s*/, '');
  }
  return 'Action failed.';
}

function capitaliseRole(role: 'admin' | 'caregiver'): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function shortUid(uid: string): string {
  return uid.length <= 8 ? uid : `${uid.slice(0, 4)}…${uid.slice(-4)}`;
}

function InviteDialog({ aid, onInviteSent }: { aid: string; onInviteSent: () => void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'caregiver' | 'admin'>('caregiver');
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>Invite caregiver</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Invite a caregiver</DialogTitle>
        <DialogDescription>
          They'll get an email with a link. The link expires in 7 days.
        </DialogDescription>
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            setBusy(true);
            setInfo(null);
            setError(null);
            try {
              await createInvite({ accountId: aid, email, role });
              setInfo(`Invite sent to ${email}.`);
              setEmail('');
              onInviteSent();
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Invite failed.');
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label="Email" htmlFor="invite-email">
            <Input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Role" htmlFor="invite-role">
            <select
              id="invite-role"
              className="h-10 w-full rounded-lg border border-paper-border bg-white px-3 text-sm text-ink"
              value={role}
              onChange={(e) => setRole(e.target.value as 'caregiver' | 'admin')}
            >
              <option value="caregiver">Caregiver</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          {info && (
            <div className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">{info}</div>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button type="submit" disabled={busy || !email}>
              {busy ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
